import { z } from 'zod';

import { PowerLevel } from '../validators/fields/PowerLevel';
import { RTESchema } from '../validators/RTE';
import { FeatureFlags } from './FeatureFlags';
import { MeasurementSystem } from './MeasurementSystem';
import { UserEmailUpdate } from './UserEmailUpdates';
import { UserTip } from './UserTip';

export enum UserIndustry {
    CONSULTING = 'Consulting',
    DEVELOPER = 'Developer',
    DISTRIBUTOR = 'Distributor',
    ENGINEERING_FIRM = 'Engineering Firm',
    ENERGY_STORAGE = 'Energy Storage',
    EPC_CONTRACTOR = 'EPC Contractor',
    MANUFACTURER = 'Original Equipment Manufacturers',
    SOLAR = 'Solar',
    UNIVERSITY_EDUCATION = 'University/Education',
    UTILITY = 'Utility',
    OTHER = 'Other',
}

export enum UserAccess {
    ADMIN_PANEL = 'adminPanel', // can access Payload backend
    INTERNAL = 'internal',
    DEVELOPER = 'developer', // can see more info in the UI, can see unreleased features
}

export enum UserProgressItem {
    CREATE_PROJECT = 'createProject',
    INVITE_TEAM_USERS = 'inviteTeamUsers',
    CREATE_PROFILE = 'createProfile',
    CREATE_PRODUCT = 'createProduct',
    CREATE_REFERENCE_DESIGN = 'createReferenceDesign',
}

export enum UserReferrer {
    REPLUS = 'replus',
    REDDIT = 'reddit',
    MAP_YOUR_SHOW = 'map-your-show',
}

export enum UserType {
    DESIGNER = 'designer',
    MANUFACTURER = 'manufacturer',
}

export enum UserAgreementType {
    FAULT_SIMULATION_DISCLAIMER = 'faultSimulationDisclaimer',
}

export const userSchema = z.object({
    id: z.string(),
    collection: z.literal('users'),
    team: z.string(),
    name: z.string().optional(),
    goofy: z.string().optional(),
    email: z.string().toLowerCase().email(),
    profileImage: z.string().optional().nullable(),
    source: z.string().optional(),
    developer: z.boolean().optional(),
    internal: z.boolean().optional(),
    adminPanel: z.boolean().optional(),
    order: z.any(), // todo..
    updatedAt: z.string(), // TODO
    createdAt: z.string(), // TODO
    lastActiveDate: z.string().nullable().optional(),
    dismissedTips: z.array(z.nativeEnum(UserTip)),
    completedProgressItems: z.array(z.nativeEnum(UserProgressItem)),
    flags: z.array(z.nativeEnum(FeatureFlags)),
    phone: z.string().optional(),
    signup: z
        .object({
            company: z.string().optional(),
            job: z.string().optional(),
            industry: z.nativeEnum(UserIndustry).optional().nullable(),
            industryOther: z.string().nullable().optional(),
            referral: z.string().optional(),
            systemSize: PowerLevel.validator.optional(),
        })
        .optional(),
    measurementSystem: z.nativeEnum(MeasurementSystem).optional(),
    type: z.nativeEnum(UserType).optional(),
    referrer: z.nativeEnum(UserReferrer).optional(),
    isManufacturer: z.boolean().optional(),
    hasCompany: z.boolean().optional(),
    isSupportUser: z.boolean().optional(),
    dismissedFeatureSuggestions: z
        .object({
            feature: z.string(),
            data: z.string(),
        })
        .array(),
    teamSuggestions: z
        .object({
            team: z.string(),
            reason: z.string(),
        })
        .array()
        .optional(),
    teamSuggestionsDismissed: z.boolean().optional(),
    companySuggestions: z
        .object({
            company: z.string(),
            reason: z.string(),
        })
        .array()
        .optional(),
    companySuggestionsDismissed: z.boolean().optional(),
    designAIAssistantGuidelines: z.string().default(''),
    redirect: z.string().optional(),
    emailPreferences: z.record(z.nativeEnum(UserEmailUpdate), z.boolean()).optional(),
    agreements: z
        .object({
            type: z.nativeEnum(UserAgreementType),
            date: z.string(),
        })
        .array()
        .optional(),
});

export type User = z.infer<typeof userSchema>;

export const userInvitationSchema = z.object({
    id: z.string(),
    email: z.string().toLowerCase().email(),
    user: z.string().optional(),
    references: z.object({
        company: z.string(),
    }),
    message: RTESchema.optional().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});

export type UserInvitation = z.infer<typeof userInvitationSchema>;
